import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import convertToNoDecimalValue from "../../../../../utils/convertToNoDecimalValue";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import {
  LoaderSizes,
  ThickNessLevel,
} from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import CircleMinus from "../../../../../common/assets/icons/CircleMinus";
import ResizeObserver from "resize-observer-polyfill";
import SLAModal from "./sla-modal";
import { useCartCheckoutPermissions } from "../../../../../hooks/useCartCheckoutPermissions";
import { useServiceCatalogPermissions } from "../../../../../hooks/useServiceCatalogPermissions";
import { getSubscriptionButtonState } from "../../../../../utils/subscriptionUtils";
import "./FeatureCarousel.scss";

interface FeatureCarouselProps {
  collection: {
    id?: string;
    handle?: string;
    title?: string;
    description?: string;
    products?: {
      nodes?: {
        title?: string;
        id?: string;
        description?: string;
        descriptionHtml?: string;
        productType?: string;
        priceRangeV2?: {
          minVariantPrice?: {
            amount?: string;
            currencyCode?: string;
          };
          maxVariantPrice?: {
            amount?: string;
            currencyCode?: string;
          };
        };
        featuredImage?: {
          url?: string;
        };
        metafields: {
          nodes: Array<{
            key: string;
            value: string;
            definition: {
              name: string;
            };
          }>;
        };
        variants?: {
          edges?: {
            node?: {
              sku?: string;
              id?: string;
            };
          }[];
        };
        tags?: string[];
        sellingPlanGroups: {
          edges: {
            node: {
              sellingPlans: {
                edges: {
                  node: {
                    id: string;
                  };
                }[];
              };
            };
          }[];
        };
      }[];
    };
  };
  onAddToCartClick: (
    productVariantId: string,
    sellingPlanID: string | undefined
  ) => void;
}

const FeatureCarousel: React.FC<FeatureCarouselProps> = ({
  collection,
  onAddToCartClick,
}) => {
  const [showCarouselButton, setShowCarouselButton] =
    React.useState<boolean>(false);
  const [slaModalOpen, setSlaModalOpen] = React.useState<boolean>(false);
  const [slaDetailSKU, setSlaDetailSKU] = React.useState<string>("");
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { addToCartLoading, addedProductID, supportServiceInCart } =
    useAppSelector((state) => state.cart);
  const { contractStatusList, metafieldKeyOrder, status } = useAppSelector(
    (state) => state.plp
  );
  const { showCartButton, cartButtonDisabled, canEdit } =
    useCartCheckoutPermissions();
  const { actionsLocked: catalogActionsLocked } = useServiceCatalogPermissions();

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        let newWidth = 0;
        if (entry.contentBoxSize) {
          newWidth =
            entry.contentBoxSize[0]?.inlineSize || entry.contentRect.width;
        } else {
          newWidth = entry.contentRect.width;
        }
        if (newWidth <= 871) {
          setShowCarouselButton(true);
        } else {
          setShowCarouselButton(false);
        }
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const features = React.useMemo(() => {
    if (
      metafieldKeyOrder[0] &&
      metafieldKeyOrder[0].metadata &&
      metafieldKeyOrder[0].metadata.length > 0
    ) {
      return metafieldKeyOrder[0].metadata;
    }
    // Fallback to hardcoded array if API data is not available yet
    console.log("Using fallback hardcoded metafield order");
    return [
      { key: "development_support_validity" },
      { key: "sla_management" },
      { key: "support" },
      { key: "test_hardware_signo_reader" },
      { key: "origo_integration_sandbox_credentials" },
      { key: "developer_guide_and_samples" },
      { key: "solution_validation_development" },
      { key: "training_consultant" },
      { key: "dedicated_consultants" },
      { key: "technical_cadence" },
      { key: "escalation_manager" },
      { key: "developer_certification" },
      { key: "beta_program" },
      { key: "quality_consultant" },
      { key: "solution_certification_post_launch" },
    ];
  }, [metafieldKeyOrder]);

  // Show loading state while fetching metafield data
  if (
    status.metafieldKeyOrderLoading &&
    (!metafieldKeyOrder ||
      metafieldKeyOrder.length === 0 ||
      !metafieldKeyOrder[0]?.metadata)
  ) {
    return (
      <div className="feature-carousel-container">
        <div className="product-carousel">
          <div className="loader-container">
            <Loader variant={LoaderSizes.large} />
          </div>
        </div>
      </div>
    );
  }

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollLeft -= 224;
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollLeft += 224;
    }
  };

  const valueForKey = (
    features: Array<{
      key: string;
      value: string;
    }>,
    key: string
  ): string => {
    for (let i = 0; i < features.length; i++) {
      if (features[i]?.key === key) {
        if (["development_support_validity", "term"].includes(key))
          return features[i].value + " Months";
        else return features[i].value;
      }
    }
    return "NA";
  };

  const nameForKey = (
    features: Array<{
      key: string;
      value: string;
      definition: {
        name: string;
      };
    }>,
    key: string
  ): string => {
    for (let i = 0; i < features.length; i++) {
      if (features[i]?.key === key) {
        return features[i].definition?.name;
      }
    }
    return "NA";
  };

  return (
    <div className="feature-carousel-container">
      <div className="feature-list">
        {features.map((feature, index) => (
          <div className="feature-label" key={index}>
            {nameForKey(
              collection?.products?.nodes[0].metafields.nodes,
              feature.key
            )}
          </div>
        ))}
      </div>
      <div className="product-carousel">
        <div className="carousel-container" ref={containerRef}>
          {collection?.products?.nodes.map((product: any, index: number) => {
            const buttonState = getSubscriptionButtonState(
              contractStatusList,
              product?.variants?.edges[0]?.node?.sku
            );
            const addToCartLoadingStatus =
              addToCartLoading &&
              addedProductID === product.variants.edges[0].node.id;
            const sellingPlanID =
              product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges[0]
                ?.node.id;
            const addToCartDisabled =
              supportServiceInCart || buttonState.disabled;
            const isScheduledOrActivePackage =
              buttonState.label === "Active" ||
              buttonState.label === "Scheduled";
            return (
              <div className="feature-service-card" key={product.id}>
                <div className="info">
                  <div className="image-wrapper">
                    <img src={product.featuredImage?.url} alt="product-image" />
                  </div>
                  <div className="name">{product.title}</div>
                  <div className="price">
                    ${" "}
                    {convertToNoDecimalValue(
                      product.priceRangeV2.maxVariantPrice.amount
                    )}
                  </div>

                  <div className="subscription">
                    {sellingPlanID ? "Subscription" : "One Time"}
                  </div>
                  <div className="part-number">
                    {product?.variants?.edges[0]?.node?.sku}
                  </div>

                  <div className="cart-button">
                    {isScheduledOrActivePackage ? (
                      <Button
                        label={buttonState.label}
                        variant="override"
                        icon={<Iconography icon="circleCheck" />}
                        disabled={catalogActionsLocked}
                        onClick={() => {}}
                      />
                    ) : showCartButton ? (
                      <>
                        <Button
                          label={buttonState.label}
                          variant="primary"
                          icon={<Iconography icon="cartShopping" />}
                          disabled={
                            catalogActionsLocked ||
                            cartButtonDisabled ||
                            addToCartLoadingStatus ||
                            addToCartDisabled ||
                            product?.variants?.edges?.[0]?.node?.sku ===
                              "PS-SUPPORT-T300"
                          }
                          onClick={() => {
                            if (!canEdit || catalogActionsLocked) return;
                            onAddToCartClick(
                              product.variants.edges[0].node.id,
                              sellingPlanID
                            );
                          }}
                        />
                        {addToCartLoadingStatus && (
                          <Loader
                            variant={LoaderSizes.small}
                            thicknessLevel={ThickNessLevel.thick}
                          />
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
                {features.map((feature, index: number) => {
                  const value = valueForKey(
                    product.metafields.nodes,
                    feature.key
                  );
                  const borderNone = index === 0 ? " border-none" : "";
                  if (value.toString().toLowerCase() === "yes") {
                    return (
                      <div
                        className={`feature-value circle-check${borderNone}`}
                        key={feature.key}
                      >
                        <Iconography
                          icon="circleCheck"
                          size={IconographySizes.Medium}
                        />
                      </div>
                    );
                  } else if (value.toString().toLowerCase() === "no") {
                    return (
                      <div
                        className={`feature-value circle-minus${borderNone}`}
                        key={feature.key}
                      >
                        <CircleMinus />
                      </div>
                    );
                  }
                  return (
                    <div
                      className={`feature-value${borderNone}`}
                      key={feature.key}
                    >
                      {product.variants?.edges[0]?.node?.sku !==
                        "ORIGO-TPS-OMI-STARTER" &&
                      feature.key === "sla_management" ? (
                        <Button
                          label="View Details"
                          variant="secondary"
                          onClick={() => {
                            setSlaDetailSKU(
                              product.variants?.edges[0]?.node?.sku
                            );
                            setSlaModalOpen(true);
                          }}
                        />
                      ) : (
                        <div className="feature-box">{value}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {showCarouselButton && (
          <div className="button-group">
            <Button
              variant="secondary"
              icon={
                <Iconography
                  icon="circleChevronRight"
                  size={IconographySizes.Medium}
                />
              }
              onClick={scrollRight}
            />
            <Button
              variant="secondary"
              icon={
                <Iconography
                  icon="circleChevronLeft"
                  size={IconographySizes.Medium}
                />
              }
              onClick={scrollLeft}
            />
          </div>
        )}
      </div>
      {slaModalOpen && (
        <SLAModal
          collection={collection}
          setDetailModalOpen={setSlaModalOpen}
          slaDetailSKU={slaDetailSKU}
        />
      )}
    </div>
  );
};

export default FeatureCarousel;
